<template>
  <div class="alerts-shell" style="margin-top: 20px">

    <!-- Marketing pitch for tenants without Alerts access -->
    <div v-if="showMarketingPage" class="rd-upsell">
      <div class="rd-upsell-icon-wrap">
        <i class="fa fa-bell rd-upsell-icon"></i>
      </div>
      <h2 class="rd-upsell-headline">Unlock Alerts &amp; Notifications</h2>
      <p class="rd-upsell-sub">
        Stay ahead of dispatch issues with automated alerts delivered by email, SMS, and voice.
      </p>
      <ul class="rd-upsell-list">
        <li>
          <i class="fa fa-check-circle"></i>
          <span><strong>Late Alerts</strong> — notify drivers and dispatch when bookings or vehicles are running behind schedule</span>
        </li>
        <li>
          <i class="fa fa-check-circle"></i>
          <span><strong>Trip Notifications</strong> — keep customers and drivers informed with automated pre-trip and post-trip messages</span>
        </li>
        <li>
          <i class="fa fa-check-circle"></i>
          <span><strong>Vehicle Activity Alerts</strong> — detect unplanned movement, inactivity, gateway disconnects, and ELD issues</span>
        </li>
        <li>
          <i class="fa fa-check-circle"></i>
          <span><strong>Group Messaging</strong> — broadcast urgent updates to drivers and staff from one place</span>
        </li>
      </ul>
      <router-link
        :to="{ name: 'support_ticket_create', query: { category: 'alerts', subject: 'I\'d like to sign up for Alerts & Notifications' } }"
        class="btn rd-upsell-cta"
      >
        <i class="fa fa-ticket"></i> Open a support ticket
      </router-link>
    </div>

    <!-- Nav shell for tenants with access: Dashboard + Alert History -->
    <div v-else class="as-layout">
      <div class="as-nav-wrapper" :class="{ 'as-nav-collapsed': navCollapsed }">
        <div class="as-nav-inner">
          <router-link :to="{ name: 'alerts_dashboard' }" class="as-nav-home" :class="{ active: $route.name === 'alerts_dashboard' }">
            <i class="fa fa-th-large as-nav-home-icon"></i>
            Dashboard
          </router-link>
          <router-link :to="{ name: 'alert_list' }" class="as-nav-sub-item" :class="{ active: $route.name === 'alert_list' }">
            <i class="fa fa-history as-nav-sub-icon"></i>Alert History
          </router-link>
          <router-link :to="{ name: 'delivery_failures' }" class="as-nav-sub-item" :class="{ active: $route.name === 'delivery_failures' }">
            <i class="fa fa-exclamation-triangle as-nav-sub-icon"></i>Delivery Failures
          </router-link>
          <router-link
            v-if="showGroupMessageNav"
            :to="{ name: groupMessageRoute }"
            class="as-nav-sub-item"
            :class="{ active: $route.name === 'group_message_sender' || $route.name === 'group_message_sender_tbn' }"
          >
            <i class="fa fa-bullhorn as-nav-sub-icon"></i>Group Message
          </router-link>
        </div>
        <button
          class="as-nav-toggle"
          @click="toggleNav"
          :title="navCollapsed ? 'Expand menu' : 'Collapse menu'"
          :aria-label="navCollapsed ? 'Expand menu' : 'Collapse menu'"
        >
          <i class="fa" :class="navCollapsed ? 'fa-chevron-right' : 'fa-chevron-left'"></i>
        </button>
      </div>
      <div class="as-content">
        <router-view />
      </div>
    </div>

  </div>
</template>

<script>
import { mapState } from 'vuex'
import { hasAlertsAccess, hasAlertsHistoryAccess, hasGroupMessageAccess, groupMessageRouteName } from '../utils/alerts_access'

export default {
  name: 'alerts_shell',
  data() {
    return {
      navCollapsed: false,
      navUserToggled: false,
    }
  },
  computed: {
    ...mapState({
      user: state => state.profile.user,
    }),
    features() {
      return (this.user.features && this.user.features[this.user.tenant]) || {}
    },
    showMarketingPage() {
      if (this.$route.query.upsell) {
        return !hasAlertsAccess(this.features)
      }
      return !hasAlertsHistoryAccess(this.features)
    },
    showGroupMessageNav() {
      return hasGroupMessageAccess(this.user)
    },
    groupMessageRoute() {
      return groupMessageRouteName(this.user)
    },
  },
  mounted() {
    this._onResize = () => {
      // Keep the nav responsive to window width unless the user has explicitly toggled it
      if (this.navUserToggled) return
      this.navCollapsed = window.innerWidth < 1600
    }
    window.addEventListener('resize', this._onResize)
    this._onResize()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this._onResize)
  },
  methods: {
    toggleNav() {
      this.navCollapsed = !this.navCollapsed
      this.navUserToggled = true
    },
  },
}
</script>

<style scoped>
.rd-upsell {
  max-width: 640px;
  margin: 40px auto;
  text-align: center;
  padding: 40px 32px;
  border: 2px solid #b8d0e4;
  border-radius: 10px;
  background: linear-gradient(180deg, #f0f6fc 0%, #fff 100%);
}
.rd-upsell-icon-wrap { margin-bottom: 16px; }
.rd-upsell-icon { font-size: 48px; color: var(--fi-rail-accent); }
.rd-upsell-headline { font-size: 24px; font-weight: bold; color: #1a3a5c; margin-bottom: 10px; }
.rd-upsell-sub { font-size: 15px; color: #555; margin-bottom: 24px; }
.rd-upsell-list { list-style: none; padding: 0; margin: 0 0 28px; text-align: left; display: inline-block; }
.rd-upsell-list li { display: flex; align-items: flex-start; gap: 10px; margin-bottom: 12px; font-size: 14px; color: #333; }
.rd-upsell-list .fa-check-circle { color: var(--fi-rail-accent); font-size: 16px; margin-top: 2px; flex-shrink: 0; }
.rd-upsell-cta { background-color: var(--fi-rail-accent); color: #fff !important; font-size: 15px; font-weight: bold; padding: 10px 28px; border-radius: 6px; text-decoration: none; border: none; }
.rd-upsell-cta:hover { background-color: #2e6da4; color: #fff !important; text-decoration: none; }
.rd-upsell-cta .fa { margin-right: 6px; }

/* ── Nav shell (mirrors booking_shell.vue) ──────────────────────────── */
.as-layout {
  display: flex;
  align-items: flex-start;
}

.as-nav-wrapper {
  position: relative;
  display: flex;
  flex-shrink: 0;
}

.as-nav-inner {
  width: 250px;
  min-width: 250px;
  overflow: hidden;
  transition: width 0.22s ease, min-width 0.22s ease;
  background-color: #e8eef5;
  border-right: 2px solid #dee2e6;
  padding: 12px 0;
}

.as-nav-collapsed .as-nav-inner {
  width: 0;
  min-width: 0;
  border-right: none;
  padding: 0;
}

.as-nav-home {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  font-size: 15px;
  font-weight: 600;
  color: #333 !important;
  text-decoration: none !important;
  border-bottom: 2px solid #c8d8e8;
  white-space: nowrap;
  cursor: pointer;
  background: transparent;
  transition: background 0.12s;
}

.as-nav-home:hover {
  background-color: #d0dfee;
  text-decoration: none !important;
}

.as-nav-home.active {
  background-color: steelblue !important;
  color: white !important;
  border-bottom-color: #f0f0f0;
}

.as-nav-home-icon {
  margin-right: 7px;
  font-size: 12px;
}

.as-nav-sub-icon {
  margin-right: 6px;
  width: 14px;
  text-align: center;
}

.as-nav-sub-item {
  display: block;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  color: #333 !important;
  text-decoration: none !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  border-left: 3px solid transparent;
  transition: background 0.1s, border-color 0.1s;
}

.as-nav-sub-item:hover {
  background-color: #dce8f5;
  text-decoration: none !important;
}

.as-nav-sub-item.active {
  background-color: #dce8f5;
  border-left-color: steelblue;
  color: steelblue !important;
  font-weight: 600;
}

.as-nav-toggle {
  position: absolute;
  top: 12px;
  right: -20px;
  width: 20px;
  height: 36px;
  background: #e8eef5;
  border: 1px solid #c8d8e8;
  border-left: none;
  border-radius: 0 4px 4px 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4a6785;
  font-size: 11px;
  padding: 0;
  z-index: 10;
  transition: background 0.15s;
}

.as-nav-toggle:hover {
  background: #d0dfee;
  color: steelblue;
}

.as-nav-toggle:focus {
  outline: none;
}

.as-content {
  flex: 1;
  min-width: 0;
  padding-left: 32px;
}
</style>
